import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { MonitoringCategorySection } from "@domain/project/monitoring-product-list/monitoring-category-section";
import { MonitoringConfigProductMap } from "@domain/project/monitoring-product-list/monitoring-config-product-map";
import { assignConfigSubPositionIds } from "@domain/project/product-list/product-list-position.util";
import { ALPHABET } from "@utils/alphabet";
import { DisplayConfiguration } from "../configurations/monitoring/display-configuration";
import { TelemetryConfiguration } from "../configurations/monitoring/telemetry-configuration";
import { FloorplanDisplay } from "../floorplan/monitoring/floorplan-display";

export class MonitoringGroupSection {
  name: string = "";
  floorplanName: string = "";
  patientMonitors!: MonitoringCategorySection<
    FloorplanPatientMonitor,
    FloorplanSimplePlaceholder,
    PatientMonitorConfiguration,
    MonitoringConfigProductMap
  >;
  centralStations!: MonitoringCategorySection<
    FloorplanCentralStation,
    FloorplanSimplePlaceholder,
    CentralStationConfiguration,
    MonitoringConfigProductMap
  >;
  telemetries!: MonitoringCategorySection<
    FloorplanTelemetry,
    FloorplanSimplePlaceholder,
    TelemetryConfiguration,
    MonitoringConfigProductMap
  >;
  kvms!: MonitoringCategorySection<FloorplanKvm, FloorplanSimplePlaceholder, KvmConfiguration, MonitoringConfigProductMap>;
  displays!: MonitoringCategorySection<
    FloorplanDisplay,
    FloorplanSimplePlaceholder,
    DisplayConfiguration,
    MonitoringConfigProductMap
  >;
  utilities!: MonitoringCategorySection<
    FloorplanUtility,
    FloorplanSimplePlaceholder,
    UtilityConfiguration,
    MonitoringConfigProductMap
  >;
  constructor(name: string, floorplanName?: string) {
    this.name = name;
    this.floorplanName = floorplanName ?? "";
  }

  createPatientMonitors(
    devices: FloorplanPatientMonitor[],
    placeholders: FloorplanSimplePlaceholder[],
    mappings: MonitoringConfigProductMap[],
  ) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });
    mappings.forEach((patientMonitor) => {
      const config = mappings.find((mapping) => patientMonitor.config.id === mapping.config.id);
      if (patientMonitor.data) this.setPositionIdForConfigData(config!);
    });
    this.patientMonitors = new MonitoringCategorySection(devices, placeholders, mappings, MonitoringCategory.PATIENT_MONITORS);
  }

  createCentralStations(
    devices: FloorplanCentralStation[],
    placeholders: FloorplanSimplePlaceholder[],
    mappings: MonitoringConfigProductMap[],
  ) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });
    mappings.forEach((mapping) => {
      const config = mappings.find((m) => mapping.config.id === m.config.id);
      if (mapping.data) this.setPositionIdForConfigData(config!);
    });
    devices.forEach((centralStation) => {
      const config = mappings.find((mapping) => centralStation.config.id === mapping.config.id);
      if (centralStation.products) this.setPositionIdForCustomProducts(centralStation, config!);
    });
    this.centralStations = new MonitoringCategorySection(devices, placeholders, mappings, MonitoringCategory.CENTRAL_STATIONS);
  }

  createTelemetries(
    devices: FloorplanTelemetry[],
    placeholders: FloorplanSimplePlaceholder[],
    mappings: MonitoringConfigProductMap[],
  ) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });
    mappings.forEach((mapping) => {
      const config = mappings.find((m) => mapping.config.id === m.config.id);
      if (mapping.data) this.setPositionIdForConfigData(config!);
    });
    devices.forEach((telemetry) => {
      const config = mappings.find((mapping) => telemetry.config.id === mapping.config.id);
      if (telemetry.products) this.setPositionIdForCustomProducts(telemetry, config!);
    });
    this.telemetries = new MonitoringCategorySection(devices, placeholders, mappings, MonitoringCategory.TELEMETRY);
  }

  createKvms(devices: FloorplanKvm[], placeholders: FloorplanSimplePlaceholder[], mappings: MonitoringConfigProductMap[]) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });
    mappings.forEach((mapping) => {
      const config = mappings.find((m) => mapping.config.id === m.config.id);
      if (mapping.data) this.setPositionIdForConfigData(config!);
    });
    devices.forEach((kvm) => {
      const config = mappings.find((mapping) => kvm.config.id === mapping.config.id);
      if (kvm.products) this.setPositionIdForCustomProducts(kvm, config!);
    });
    this.kvms = new MonitoringCategorySection(devices, placeholders, mappings, MonitoringCategory.KVMS);
  }

  createDisplays(
    devices: FloorplanDisplay[],
    placeholders: FloorplanSimplePlaceholder[],
    mappings: MonitoringConfigProductMap[],
  ) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });
    mappings.forEach((mapping) => {
      const config = mappings.find((m) => mapping.config.id === m.config.id);
      if (mapping.data) this.setPositionIdForConfigData(config!);
    });
    devices.forEach((display) => {
      const config = mappings.find((mapping) => display.config.id === mapping.config.id);
      if (display.products) this.setPositionIdForCustomProducts(display, config!);
    });
    this.displays = new MonitoringCategorySection(devices, placeholders, mappings, MonitoringCategory.DISPLAYS);
  }

  createUtilities(
    devices: FloorplanUtility[],
    placeholders: FloorplanSimplePlaceholder[],
    mappings: MonitoringConfigProductMap[],
  ) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });
    mappings.forEach((mapping) => {
      const config = mappings.find((m) => mapping.config.id === m.config.id);
      if (mapping.data) this.setPositionIdForConfigData(config!);
    });
    devices.forEach((utility) => {
      const config = mappings.find((mapping) => utility.config.id === mapping.config.id);
      if (utility.products) this.setPositionIdForCustomProducts(utility, config!);
    });
    this.utilities = new MonitoringCategorySection(devices, placeholders, mappings, MonitoringCategory.UTILITY);
  }

  private setPositionIdForPlaceholderProduct(placeholder: FloorplanSimplePlaceholder) {
    if (placeholder.products.length > 0) {
      placeholder.products.forEach((product, index) => {
        if (product.id) {
          product.positionId = `${placeholder.positionId?.split(".")[0]}.${ALPHABET[index]}`;
        }
      });
    }
  }

  private setPositionIdForCustomProducts(
    product:
      FloorplanPatientMonitor | FloorplanCentralStation | FloorplanTelemetry | FloorplanKvm | FloorplanDisplay | FloorplanUtility,
    config: MonitoringConfigProductMap,
  ) {
    if (product.products.length > 0) {
      const lastPositionId = config.data[config.data.length - 1]?.positionId;
      if (!lastPositionId) return;

      const [baseId, lastLetter] = lastPositionId.split(".");
      const startIndex = Math.max(ALPHABET.indexOf(lastLetter) + 1, 0);
      product.products.forEach((p, index) => {
        if (p.id) {
          p.positionId = `${baseId}.${ALPHABET[startIndex + index]}`;
        }
      });
    }
  }
  private setPositionIdForConfigData(config: MonitoringConfigProductMap) {
    assignConfigSubPositionIds(config.data);
  }
}
